import { useEffect, useState } from 'react';
import API_BASE from '../utils/api.js';

function normalizeProject(raw) {
  return {
    id:
      raw.projectId ||
      raw.id ||
      raw.name ||
      (raw.userId && (raw.projectNickname || raw.title)) ||
      JSON.stringify(raw),
    name:
      raw.projectNickname || raw.name || raw.title || raw.projectNickname || raw.id || 'Untitled',
    createdAt: raw.createdAt || raw.created_at || raw.created || null,
    updatedAt: raw.updatedAt || raw.updated_at || raw.updated || null,
    isActive: raw.isActive ?? true,
    raw,
  };
}

export default function useProjects({ activeOnly = true, isOpen = true } = {}) {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const userId = localStorage.getItem('userId');
    if (!userId || !isOpen) {
      setProjects([]);
      return;
    }

    const controller = new AbortController();
    const signal = controller.signal;

    async function loadProjects() {
      setLoading(true);
      setError(null);

      const apiBase = API_BASE;

      const candidates = [
        `${apiBase}/api/projects/${encodeURIComponent(userId)}`,
        `${apiBase}/api/projects?userId=${encodeURIComponent(userId)}&activeOnly=${activeOnly}`,
      ];

      let got = null;

      for (const url of candidates) {
        try {
          const res = await fetch(url, { signal, cache: 'no-store' });
          if (!res.ok) continue;
          const ct = (res.headers.get('content-type') || '').toLowerCase();
          if (ct.includes('json')) {
            got = await res.json();
            break;
          }
        } catch (e) {
          if (e.name === 'AbortError') return;
        }
      }

      if (!got) {
        setProjects([]);
        setLoading(false);
        setError('Failed to load projects');
        return;
      }

      let data = null;
      if (Array.isArray(got)) data = got;
      else if (Array.isArray(got.Items)) data = got.Items;
      else if (Array.isArray(got.items)) data = got.items;
      else data = [got];

      const normalized = data
        .map(normalizeProject)
        .filter((p) => !activeOnly || p.isActive !== false)
        .sort((a, b) => {
        const aDate = new Date(a.updatedAt || a.createdAt || 0).getTime();
        const bDate = new Date(b.updatedAt || b.createdAt || 0).getTime();
        return bDate - aDate;
      });

      setProjects(normalized);
      setLoading(false);
    }

    loadProjects();

    return () => controller.abort();
  }, [activeOnly, isOpen]);

  return { projects, loading, error };
}
